import { Column, ColumnEditorOptions, ColumnSortEvent } from "primereact/column";
import { ChecklistReviewRow, ReviewColumnOptions } from "../types/reviewAnswer";
import { SectionFilter } from "../components/Answer/SectionFilter";
import { AnswerEditor } from "../components/Shared/AnswerEditor";
import { QuestionImageCell } from "../components/Answer/QuestionImageCell";

type TranslateFunction = (key: string) => string;

// Filter config lives in reviewFilters (component-free) so the Redux slice can
// import it without pulling in these cell components. Re-exported here for the
// table components that already import filters + columns from this module.
export { defaultReviewFilters, reviewGlobalFilterFields } from "./reviewFilters";

/**
 * Sort the Response column treating an unanswered (blank) response as the
 * smallest value, so unanswered questions cluster at the top (ascending) or
 * bottom (descending). PrimeReact's default sort treats "" as null-like and
 * pins it via nullSortOrder, which prevents toggling it between ends; a plain
 * string compare keeps "" as the lowest value and restores that control
 */
const sortByResponse = (event: ColumnSortEvent) => {
    const order = event.order ?? 1;
    // Coerce: an in-progress cell edit can transiently leave response null or
    // non-string before the row recomputes, and localeCompare throws on null.
    const text = (row: ChecklistReviewRow) => String(row.response ?? "");
    return [...(event.data as ChecklistReviewRow[])].sort(
        (a, b) => order * text(a).localeCompare(text(b))
    );
};

// Responses range from "Yes" to full paragraphs, so the cell wraps and the row
// grows to keep the whole answer visible rather than truncating it. A row whose
// last save failed shows a Retry icon to re-send without re-entering the value.
const makeResponseBody = (
    t: TranslateFunction,
    editable: boolean,
    failedDataDictionaryIds: string[],
    onRetry?: (row: ChecklistReviewRow) => void,
) => (row: ChecklistReviewRow) => {
    // Table responses are a nested grid we don't edit inline yet — no pencil.
    const cellEditable = editable && row.questionType !== "table";
    const failed = !!row.dataDictionaryId && failedDataDictionaryIds.includes(row.dataDictionaryId);
    return (
        <>
            <span title={row.response || undefined}>
                {row.response}
            </span>
            {cellEditable && (
                <i className="pi pi-pencil review-response-edit-icon" aria-hidden="true" />
            )}
            {failed && onRetry && (
                // stopPropagation so the click re-saves rather than opening the cell editor.
                <button
                    type="button"
                    className="review-response-retry-icon"
                    title={t("answers.table.retry")}
                    onClick={(e) => { e.stopPropagation(); onRetry(row); }}
                >
                    <i className="pi pi-refresh" aria-hidden="true" />
                </button>
            )}
        </>
    );
};

/**
 * Builds the Checklist Review columns; the Response column is inline-editable
 * when `editable` is set.
 */
export const reviewAnswerColumns = (
    t: TranslateFunction,
    { editable = false, questionMap, onCellEditComplete, failedDataDictionaryIds = [], onRetry, onCommit }: ReviewColumnOptions = {}
) => {
    // Reuse the shared AnswerEditor, seeded from the row's rawValue (not the
    // formatted `response`)
    const responseEditor = (options: ColumnEditorOptions) => {
        const row = options.rowData as ChecklistReviewRow;
        const question = questionMap?.[row.questionId];
        const type = question?.type ?? row.questionType;
        if (type === "table") {
            return <span>{row.response}</span>;
        }
        return (
            <AnswerEditor
                options={{ ...options, value: row.rawValue }}
                question={question}
                fallbackType={type}
                onCommit={onCommit ? (value) => onCommit(row, value) : undefined}
            />
        );
    };

    return [
        <Column
            key="sectionTitle"
            field="sectionTitle"
            header={t("answers.table.section")}
            filter
            filterElement={SectionFilter}
            sortable
            frozen
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="subsectionTitle"
            field="subsectionTitle"
            header={t("answers.table.subsection")}
            filter
            filterPlaceholder={t("answers.table.searchBySubsection")}
            sortable
            className="review-subsection-column"
            showFilterMenu={false}
        />,
        <Column
            key="questionText"
            field="questionText"
            header={t("answers.table.question")}
            filter
            filterPlaceholder={t("answers.table.searchByQuestion")}
            sortable
            className="review-question-column"
            showFilterMenu={false}
        />,
        <Column
            key="response"
            field="response"
            header={t("answers.table.response")}
            filter
            filterPlaceholder={t("answers.table.searchByResponse")}
            sortable
            sortFunction={sortByResponse}
            className={editable ? "review-response-column review-response-editable" : "review-response-column"}
            showFilterMenu={false}
            body={makeResponseBody(t, editable, failedDataDictionaryIds, onRetry)}
            editor={editable ? responseEditor : undefined}
            onCellEditComplete={editable ? onCellEditComplete : undefined}
        />,
        <Column
            key="images"
            header={t("answers.table.images")}
            className="review-images-column"
            body={(row: ChecklistReviewRow) => <QuestionImageCell row={row} />}
        />,
    ];
};

export type { ChecklistReviewRow };
